<template>
  <Layout>
    <div class="container">
      <div class="edit-event-container">
        <Typography weight="600" size="1.5rem" color="gray600">Edit Event</Typography>
        <template v-if="event">
          <div class="event-details" v-if="authStore.hasPolicy('school.events.general.write')">
            <div style="max-width: 100%">
              <span class="title">Event Name</span>
              <div class="items">
                <v-text-field
                  label="Event Name"
                  class="custom-input"
                  density="comfortable"
                  color="primary"
                  v-model="event.conference_name"
                  bg-color="white"
                ></v-text-field>
              </div>
            </div>
            <div>
              <span class="title"
                >Reservation Window

                <v-tooltip activator="parent" location="bottom">
                  When parents can book new meetings for this event
                </v-tooltip>
              </span>
              <div class="items">
                <DatePicker
                  class="custom-input datetime"
                  density="comfortable"
                  type="datetime-local"
                  label="Start"
                  color="primary"
                  :model-value="event.open_tstamp ? new Date(event.open_tstamp) : new Date()"
                  @update:model-value="
                    event.open_tstamp = confStore.getDayJs($event).format(`YYYY-MM-DD HH:mm:ss`)
                  "
                  data-cy="event-open-time"
                ></DatePicker>
                <span>to</span>
                <DatePicker
                  class="custom-input datetime"
                  density="comfortable"
                  type="datetime-local"
                  label="End"
                  color="primary"
                  :model-value="event.closed_tstamp ? new Date(event.closed_tstamp) : new Date()"
                  @update:model-value="
                    event.closed_tstamp = confStore.getDayJs($event).format(`YYYY-MM-DD HH:mm:ss`)
                  "
                  data-cy="event-close-time"
                ></DatePicker>
              </div>
            </div>
          </div>

          <div class="event-days-top" v-if="authStore.hasPolicy('school.events.general.write')">
            <div class="left">
              <span class="title">Event Days</span>
              <v-btn
                @click="state.showAddEvent = true"
                style="text-transform: capitalize"
                data-cy="add-event-day-btn"
                >Add Day</v-btn
              >
            </div>
            <div class="right">
              <div class="actions">
                <div class="action" @click="state.showParentConfirm = true">
                  <v-icon class="icon">mdi-card-account-mail</v-icon>
                  <span class="text">Confirmation email</span>
                </div>
                <RouterLink
                  style="text-decoration: none"
                  :to="{
                    name: 'school-edit-schedule',
                    params: { eventId }
                  }"
                  data-cy="goto-event-schedule"
                >
                  <div class="action">
                    <v-icon class="icon">mdi-calendar-plus</v-icon>
                    <span class="text">Schedules</span>
                    <v-tooltip activator="parent" location="bottom">
                      Add/remove teachers and teams from the event and view/edit their schedule
                    </v-tooltip>
                  </div>
                </RouterLink>
                <RouterLink
                  v-if="authStore.hasPolicy('school.events.reports.read')"
                  style="text-decoration: none"
                  :to="{ name: 'school-reports', params: { eventId } }"
                  data-cy="goto-reports"
                >
                  <div class="action">
                    <v-icon class="icon">mdi-chart-box</v-icon>
                    <span class="text">Reports</span>
                  </div>
                </RouterLink>
                <div
                  @click="state.optionsModal = true"
                  class="action"
                  data-cy="event-options-modal-btn"
                >
                  <v-icon class="icon">mdi-table-cog</v-icon>
                  <span class="text">Options</span>
                </div>
              </div>
            </div>
          </div>

          <eventDays
            v-if="authStore.hasPolicy('school.events.general.write')"
            :event-id="eventId"
          ></eventDays>
          <div
            class="d-flex align-center flex-wrap gap-3 mt-3"
            v-if="authStore.hasPolicy('school.events.general.write')"
          >
            <v-btn
              @click="save"
              :loading="state.loadingSave"
              style="text-transform: capitalize"
              width="80px"
              data-cy="event-save-btn"
              :data-loading="state.loadingSave ? true : undefined"
              >Save</v-btn
            >
            <v-btn
              @click="showDeleteDialog"
              style="text-transform: capitalize"
              variant="outlined"
              :loading="state.loadingDelete"
              data-cy="event-delete-btn"
              >Delete</v-btn
            >
            <v-dialog v-model="state.confirmDeletedialogVisible"  persistent max-width="450">
            <v-card>
              <v-card-title>Confirm Delete</v-card-title>
              <v-card-text>
              Are you sure you want to delete this Event?
              </v-card-text>
              <v-card-actions class="d-flex justify-space-between space-between">
                <v-btn color="primary" text @click="deleteEvent" :loading="state.loadingDelete" variant="outlined" >Delete</v-btn>
                <v-btn color="primary" text @click="state.confirmDeletedialogVisible=false" variant="flat">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          </div>
        </template>
        <div v-if="!event && confStore.loading" style="width: 100%; margin-top: 200px">
          <LoadingState text="Loading Event..."></LoadingState>
        </div>
      </div>
    </div>
  </Layout>

  <EventOptionsModal v-model:show="state.optionsModal"></EventOptionsModal>
  <AddEventDayModal v-model:show="state.showAddEvent"></AddEventDayModal>

  <ParentConfirmationModal
    v-if="event"
    v-model:show="state.showParentConfirm"
    :event="event"
    @update:event="Object.assign(event, $event)"
  ></ParentConfirmationModal>
</template>

<script setup lang="ts">
import Typography from "@/components/typography.vue";
import Layout from "./Layout.vue";
import { useConferenceStore } from "@/stores/teacher/conference";
import { computed } from "vue";
import eventDays from "@/components/school/events/eventDays.vue";
import EventOptionsModal from "@/components/school/events/eventOptionsModal.vue";
import DatePicker from "@/components/datePicker.vue";
import { watch } from "vue";
import { cloneDeep } from "lodash";
import { reactive } from "vue";
import AddEventDayModal from "@/components/school/events/addEventDayModal.vue";
import LoadingState from "@/components/loadingState.vue";
import ParentConfirmationModal from "@/components/school/events/ParentConfirmationModal.vue";
import router from "@/router";
import { sort } from "@/utils/sort";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthenticationStore } from "@/stores/authentication";
 
// import dayjs from "dayjs";
// import utc from 'dayjs/plugin/utc';
// dayjs.extend(utc);
const confStore = useConferenceStore();
const alertStore = useAlertsStore();
const authStore = useAuthenticationStore();
const props = defineProps<{ eventId: string; account?: string }>();

const state = reactive({
  optionsModal: false,
  showAddEvent: false,
  loadingSave: false,
  showParentConfirm: false,
  loadingDelete: false,
  confirmDeletedialogVisible: false
});

const showDeleteDialog = () => {
  state.confirmDeletedialogVisible=true;
  console.log("Confirming delete");
}
const setEvent = async () => {
  await confStore.loadingPromise;
  const event = confStore.conferences.find((c) => c.conference_id === +props.eventId);
  if (event) {
    confStore.selected = cloneDeep(event);
    confStore.selected.conference_days = sort(confStore.selected.conference_days, (i) => i.start);
  }
};
setEvent();
watch([() => props.eventId, () => confStore.conferences], setEvent);

const event = computed(() => confStore.selected!);


const save = async () => {
  state.loadingSave = true;
  const conferenceDays = await Promise.all(
    confStore.selected!.conference_days.map(async (day) => {
      // const end = dayjs.utc(day.end);
      const end = confStore.getDayJs(day.end);
      const res = await confStore.apiUpdateConferenceDay(day.conference_days_id, {
        start: confStore.getDayJs(day.start).set("seconds", 0).format(`YYYY-MM-DD HH:mm:ss`),
        end: confStore.getDayJs(day.start)
          .set("hours", end.hour())
          .set("minutes", end.minute())
          .set("seconds", 0)
          .format(`YYYY-MM-DD HH:mm:ss`),
        time_interval: day.time_interval
      });
      if (res?.status){
        alertStore.addAlert("success","Event Day Settings Saved");
        return res.data;
      }else{
        alertStore.addAlert("error", res.message);
      }
    })
  );
  if (conferenceDays.every(Boolean)) {
    const conference = await confStore.apiUpdateConference(confStore.selected!.conference_id, {
      conference_name: confStore.selected!.conference_name,
      open_tstamp: confStore.selected!.open_tstamp,
      closed_tstamp: confStore.selected!.closed_tstamp
    });
    if (conference?.conference_id) {
      if (
        confStore.updatedConference(conference.conference_id, {
          conference_name: conference.conference_name,
          open_tstamp: conference.open_tstamp,
          closed_tstamp: conference.closed_tstamp
        })
      )
        for (const day of conferenceDays) {
          const end = confStore.getDayJs(day.end);
          
          confStore.updatedConferenceDay(event.value.conference_id, day.conference_days_id, {
            start: confStore.getDayJs(day.start).set("seconds", 0).format(`YYYY-MM-DD HH:mm:ss`),
            end: confStore.getDayJs(day.start)
              .set("hours", end.hour())
              .set("minutes", end.minute())
              .set("seconds", 0)
              .format(`YYYY-MM-DD HH:mm:ss`),
            time_interval: day.time_interval
          });
        }
    }
  }
  state.loadingSave = false;
};

const deleteEvent = async () => {
  state.loadingDelete = true;
  await confStore.deleteConference(+props.eventId);
  state.loadingDelete = false;
  router.push({
    name: "school-events"
  });
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.edit-event-container {
  padding: 30px 0;

  .event-details {
    display: flex;
    width: 100%;
    gap: 30px;
    margin-top: 20px;
    flex-wrap: wrap;
    .title {
      color: $primary;
      font-weight: 600;
      font-size: 1.1rem;
    }
    .items {
      margin-top: 10px;
      display: flex;
      align-items: center;
      gap: 15px;
      justify-content: flex-start;
      flex-wrap: wrap;
      span {
        color: gray;
      }

      .custom-input {
        width: 350px;
        max-width: min(100%, 350px);
        &.datetime {
          width: 230px;
          max-width: min(100%, 230px);
        }
      }
    }
    :last-child {
      flex-grow: 1;
    }
  }

  .event-days-top {
    display: flex;
    justify-content: space-between;
    margin-top: 30px;
    flex-wrap: wrap;
    .left {
      display: flex;
      gap: 20px;
      align-items: center;
      .title {
        color: $primary;
        font-weight: 600;
        font-size: 1.1rem;
      }
    }

    .right {
      display: flex;
      justify-content: flex-end;
      flex-grow: 1;
      .actions {
        display: flex;
        justify-content: flex-end;
        width: 100%;
        gap: 20px;
        margin-top: 20px;
        flex-wrap: wrap;
        .action {
          color: $primary;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          padding: 5px;
          border-radius: 4px;

          &:active {
            outline: 2px solid $primary-75;
          }
          gap: 5px;
          .icon {
            font-size: 1.5rem;
            font-weight: 600;
            flex-grow: 1;
          }
          .text {
            font-weight: 600;
            text-transform: uppercase;
          }
        }
      }
    }
  }
}
</style>
